import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import type { NextBestAction } from '@post-office/confluence-next-best-action';
import {
	type BackendMessageBody,
	type DefaultMessagesResponse,
	type PlacementMessageBackendFactory,
	type PlacementMessageFrontendFactory,
	createPlacement,
} from '@post-office/placement-common-v2';
import { createDefaultPlacementMiddleware } from '@post-office/placement-common-v2/placement-middleware';
import type { SerializableRecord } from '@post-office/serializable-record';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePostOfficeSideNavMessages } from './contexts/PostOfficeSideNavMessageProvider';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { EXPERIMENTAL_MESSAGES, NOT_ENROLLED_COHORT } from './experimental-message-list';
import {
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import {
	ENABLE_RERANKING_CONFLUENCE_SIDE_NAV,
	runConfluenceSideNavPlacement,
} from './run-confluence-side-nav-placement';

const { createFrontend, createBackend } = createPlacement({
	placementId: 'confluence-side-nav',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
});

export const PlacementBackend: PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = createBackend({
	customMiddleware: createDefaultPlacementMiddleware(runConfluenceSideNavPlacement),
});

// Props passed from Product to Placement
export type PlacementFrontendProps = {
	preloadedInitialData: DefaultMessagesResponse | undefined;
	onDismiss: (messages: { messages: BackendMessageBody<SerializableRecord>[] }) => void;
	onSideNavClick: (
		nextBestActionType: NextBestAction,
		preloadedMessageState: { messages: BackendMessageBody<SerializableRecord>[] },
	) => void;
	onCallback: (callbackKey: NextBestAction) => Promise<void>;
	currentRoute?: string;
};

const confluenceSideNavStyles = css({
	display: 'flex',
	margin: token('space.150', '12px'),
});

const SORTED_NBA: NextBestAction[] = [
	'MCP_SETUP_NUDGE',
	'CONNECT_3P_TOOLS_NUDGE',
	'CREATE_WITH_ROVO_NUDGE',
	'CREATE_SPACE',
	'CREATE_PAGE',
	'CREATE_WHITEBOARD',
	'1_ON_1_LOOM_NUDGE',
	'LOOM_AI_MEETING_NOTES',
	'RESURRECTED_USER_NUDGE',
	'ROVO_EXTENSION_CTA',
];

// todo: Resolve backend ordering incorrectly
const sortMessages = (messages: BackendMessageBody<SerializableRecord>[] = []) => {
	return messages.sort((a, b) => {
		return (
			// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
			SORTED_NBA.indexOf(a?.nextBestActionType as NextBestAction) -
			// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
			SORTED_NBA.indexOf(b?.nextBestActionType as NextBestAction)
		);
	});
};

type DisplayedMessage = {
	message: BackendMessageBody<SerializableRecord>;
	experimentCohort?: string;
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	PlacementFrontendProps
> = createFrontend(
	(Messages) =>
		({
			preloadedInitialData,
			onDismiss,
			onSideNavClick,
			onCallback,
			currentRoute,
		}: PlacementFrontendProps) => {
			const [{ messages, status }, setMessageState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: getMessages(preloadedInitialData?.messages),
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const context = usePostOfficeSideNavMessages();

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'confluence-side-nav',
				},
			});

			const handleOnDismissed = useCallback(() => {
				// no more messages until the next fetch
				onDismiss({ messages: [] });

				setMessageState({ messages: undefined, status: 'done' });
			}, [onDismiss]);

			const handleOnSideNavClick = useCallback(
				(nextBestActionType: NextBestAction) => {
					// display the next message
					setMessageState((prevState) => {
						if (prevState.messages) {
							const newMessages = prevState.messages.slice(1);

							onSideNavClick(nextBestActionType, { messages: newMessages });

							return {
								messages: newMessages,
								status: 'done',
							};
						}

						onSideNavClick(nextBestActionType, { messages: [] });

						return prevState;
					});
				},
				[onSideNavClick],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessageState({
						messages: getMessages(data?.messages),
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			const displayedMessage = useMemo<DisplayedMessage | undefined>(() => {
				if (!messages?.length) {
					return;
				}

				const filteredMessages = messages.filter((message) => {
					// @ts-expect-error Property 'explicitEnableRequired' does not exist on type 'BackendMessageBody<SerializableRecord>'
					if (message.explicitEnableRequired as boolean) {
						// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
						return context?.enabledMessages.includes(message.nextBestActionType as NextBestAction);
					}

					if (message.messageTemplateId === 'rovo-extension-install-cta-left-nav') {
						// Don't show on FPS to avoid collision with existing install CTA (built outside of PO)
						if (currentRoute?.includes('/search')) {
							return false;
						}
					}

					return true;
				});

				if (!filteredMessages.length) {
					return;
				}

				// https://switcheroo.atlassian.com/ui/gates/f775d661-abc2-488d-b58c-c28fab9bd58f/key/po_enable_confluence-side-nav-exp-messages
				if (FeatureGates.checkGate('po_enable_confluence-side-nav-exp-messages')) {
					for (const message of filteredMessages) {
						const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
							(msg) => msg.messageTemplateId === message.messageTemplateId,
						);

						// Non-experimental messages are always eligible and should not be overridden by
						// later experimental messages in the order they are defined.
						if (!experimentalMessage) {
							return { message };
						}

						if (
							experimentalMessage.shouldProceedToStatsigEvaluation &&
							!experimentalMessage.shouldProceedToStatsigEvaluation()
						) {
							// This experimental message should not proceed to Statsig evaluation in the
							// current environment – skip it and continue checking later messages.
							continue;
						}

						const experimentCohort = FeatureGates.getExperimentValue<string>(
							experimentalMessage.statsigExperiment,
							experimentalMessage.statsigParameterName,
							NOT_ENROLLED_COHORT,
						);

						if (experimentalMessage.statsigTestCohortPredicate(experimentCohort)) {
							return {
								message,
								experimentCohort,
							};
						}
					}

					return undefined;
				}

				return filteredMessages[0] ? { message: filteredMessages[0] } : undefined;
			}, [messages, context, currentRoute]);

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			if (!displayedMessage) {
				return null;
			}

			const { message, experimentCohort } = displayedMessage;

			return (
				<div
					css={
						!FeatureGates.checkGate('navx-global-side-nav-confluence') && confluenceSideNavStyles
					}
				>
					<Messages.render
						{...message}
						experimentCohort={experimentCohort}
						onSideNavClick={handleOnSideNavClick}
						handleOnDismissed={handleOnDismissed}
						onCallback={onCallback}
					/>
				</div>
			);
		},
);

// When the gate messages go through orchestrations ML reranker; otherwise apply client-side NBA sort.
const getMessages = (
	messages: BackendMessageBody<SerializableRecord>[] = [],
): BackendMessageBody<SerializableRecord>[] => {
	if (FeatureGates.checkGate(ENABLE_RERANKING_CONFLUENCE_SIDE_NAV)) {
		return messages;
	}
	return sortMessages(messages);
};
